import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="min-h-screen th-bg flex items-center justify-center p-6">
        <div className="w-full max-w-lg text-center">
          {/* Animated illustration */}
          <div className="relative mx-auto mb-8 w-24 h-24">
            <div className="absolute inset-0 rounded-3xl bg-red-500/10 animate-pulse" />
            <div className="absolute inset-2 rounded-2xl bg-red-500/5 flex items-center justify-center">
              <svg
                width="40"
                height="40"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-red-400"
              >
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </div>
          </div>

          <h1 className="text-2xl font-bold th-text mb-3">Application Error</h1>
          <p className="text-sm th-text-muted mb-2 max-w-sm mx-auto">
            An unexpected error prevented this page from rendering.
          </p>
          <p className="text-xs th-text-faint mb-8 font-mono max-w-sm mx-auto truncate px-4">
            {this.state.error?.message ?? 'Unknown error'}
          </p>

          <div className="flex items-center justify-center gap-3">
            <Button onClick={this.handleReset}>Try Again</Button>
            <Button
              variant="outline"
              onClick={() => {
                window.location.href = '/';
              }}
            >
              Go Home
            </Button>
          </div>
        </div>
      </div>
    );
  }
}
